import cn from 'classnames';
import { isSameDay, isSameMonth } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { EventItem } from '../../events';
import type { CalendarEvent, RenderEvent, RenderTimeSlot } from '../../types';
import { useMonthTimeSlot, useVisibleEventCount } from './hooks';
import styles from './month-day-cell.module.css';

type MonthDayCellProperties<T extends CalendarEvent> = {
  day: Date;
  displayMonth: Date;
  events: T[];
  onMoreEventsClick?: (date: Date) => void;
  renderEvent?: RenderEvent<T>;
  renderTimeSlot?: RenderTimeSlot;
  timezone: string;
};

const MonthDayCell = <T extends CalendarEvent>({
  day,
  displayMonth,
  events,
  onMoreEventsClick,
  renderEvent,
  renderTimeSlot,
  timezone,
}: MonthDayCellProperties<T>) => {
  const { t } = useTranslation(['translationReactComponents']);
  const { isSlotIndicatorVisible, renderedTimeSlot } = useMonthTimeSlot({
    day,
    renderTimeSlot,
    timezone,
  });
  const { cellReference, visibleEventCount } = useVisibleEventCount(events.length, isSlotIndicatorVisible);

  const hiddenEventCount = events.length - visibleEventCount;
  const visibleEvents = events.slice(0, visibleEventCount);
  const zonedToday = toZonedTime(new Date(), timezone);

  return (
    <div
      className={cn(
        styles.day,
        { [styles['day-outside'] ?? '']: !isSameMonth(day, displayMonth) },
        { [styles['day-today'] ?? '']: isSameDay(day, zonedToday) },
      )}
    >
      {renderedTimeSlot}
      <div className={styles['day-number']}>{t('dateFormats.dayNumber', { date: day })}</div>
      <div className={styles['day-body']} ref={cellReference}>
        {renderTimeSlot && isSlotIndicatorVisible ? (
          <div aria-hidden="true" className={styles['slot-indicator']} />
        ) : null}
        <div className={styles['day-events']}>
          {visibleEvents.map((event) => {
            const eventItem = (
              <EventItem calendarEvent={event} mode="month" type={event.isAllDay ? 'all-day' : 'time-slot'} />
            );

            return (
              <div className={styles['day-event']} key={`${day.toISOString()}-${event.id}`}>
                {renderEvent ? renderEvent({ children: eventItem, event }) : eventItem}
              </div>
            );
          })}
        </div>
        {hiddenEventCount > 0 ? (
          <button
            className={styles.more}
            disabled={!onMoreEventsClick}
            onClick={() => onMoreEventsClick?.(fromZonedTime(day, timezone))}
            type="button"
          >
            {t('moreEvents', { count: hiddenEventCount })}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default MonthDayCell;
